import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Edit2, Trash2, X, Package, Thermometer, Scale, Clock, Hash } from 'lucide-react';
import type { Product } from '../types/Product';
import { createProduct } from '../types/Product';
interface ProductManagementProps {
  onBack: () => void;
}
export function ProductManagement({
  onBack
}: ProductManagementProps) {
  const [products, setProducts] = useState<Product[]>([{
    id: '1',
    productNumber: 'P-123123',
    productName: 'Hähnchenbrust Premium',
    referenceTemperature: 7,
    inspectionsPerSample: 10,
    timeIntervalEnabled: true,
    timeIntervalMinutes: 30,
    targetWeight: 2.5,
    createdAt: new Date('2024-01-15')
  }, {
    id: '2',
    productNumber: 'P-456789',
    productName: 'Putenschnitzel Classic',
    referenceTemperature: 7,
    inspectionsPerSample: 10,
    timeIntervalEnabled: false,
    timeIntervalMinutes: 30,
    targetWeight: 1.8,
    createdAt: new Date('2024-02-20')
  }]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [formData, setFormData] = useState({
    productNumber: '',
    productName: '',
    referenceTemperature: 7,
    inspectionsPerSample: 10,
    timeIntervalEnabled: true,
    timeIntervalMinutes: 30,
    targetWeight: 2.5
  });
  const resetForm = () => {
    setFormData({
      productNumber: '',
      productName: '',
      referenceTemperature: 7,
      inspectionsPerSample: 10,
      timeIntervalEnabled: true,
      timeIntervalMinutes: 30,
      targetWeight: 2.5
    });
  };
  const handleAddProduct = () => {
    if (formData.productNumber && formData.productName) {
      const newProduct = createProduct(formData.productNumber, formData.productName, formData.referenceTemperature, formData.inspectionsPerSample, formData.timeIntervalEnabled, formData.timeIntervalMinutes, formData.targetWeight);
      setProducts([...products, newProduct]);
      setShowAddModal(false);
      resetForm();
    }
  };
  const handleEditClick = (product: Product) => {
    setSelectedProduct(product);
    setFormData({
      productNumber: product.productNumber,
      productName: product.productName,
      referenceTemperature: product.referenceTemperature,
      inspectionsPerSample: product.inspectionsPerSample,
      timeIntervalEnabled: product.timeIntervalEnabled,
      timeIntervalMinutes: product.timeIntervalMinutes,
      targetWeight: product.targetWeight
    });
    setShowEditModal(true);
  };
  const handleUpdateProduct = () => {
    if (selectedProduct && formData.productNumber && formData.productName) {
      setProducts(products.map(p => p.id === selectedProduct.id ? {
        ...p,
        productNumber: formData.productNumber,
        productName: formData.productName,
        referenceTemperature: formData.referenceTemperature,
        inspectionsPerSample: formData.inspectionsPerSample,
        timeIntervalEnabled: formData.timeIntervalEnabled,
        timeIntervalMinutes: formData.timeIntervalMinutes,
        targetWeight: formData.targetWeight
      } : p));
      setShowEditModal(false);
      setSelectedProduct(null);
      resetForm();
    }
  };
  const handleDeleteClick = (product: Product) => {
    setSelectedProduct(product);
    setShowDeleteConfirm(true);
  };
  const handleConfirmDelete = () => {
    if (selectedProduct) {
      setProducts(products.filter(p => p.id !== selectedProduct.id));
      setShowDeleteConfirm(false);
      setSelectedProduct(null);
    }
  };
  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setSelectedProduct(null);
  };
  return <div className="space-y-6" data-id="element-627">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-628">
        <h2 className="text-2xl font-semibold text-gray-900" data-id="element-629">
          Produktverwaltung
        </h2>
        <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors" data-id="element-630">
          <Plus className="w-4 h-4" strokeWidth={2} data-id="element-631" />
          Produkt hinzufügen
        </button>
      </div>

      {/* Products List */}
      <div className="space-y-3" data-id="element-632">
        {products.length === 0 ? <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200" data-id="element-633">
            <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" strokeWidth={1.5} data-id="element-634" />
            <p className="text-gray-500" data-id="element-635">Noch keine Produkte angelegt</p>
          </div> : products.map(product => <motion.div key={product.id} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow" data-id="element-636">
              <div className="flex items-start justify-between" data-id="element-637">
                <div className="flex-1" data-id="element-638">
                  <div className="flex items-center gap-3 mb-3" data-id="element-639">
                    <div className="p-3 bg-green-100 rounded-lg" data-id="element-640">
                      <Package className="w-5 h-5 text-green-600" strokeWidth={2} data-id="element-641" />
                    </div>
                    <div data-id="element-642">
                      <h3 className="font-semibold text-gray-900" data-id="element-643">
                        {product.productName}
                      </h3>
                      <p className="text-sm text-gray-500" data-id="element-644">
                        Produkt-Nr: {product.productNumber}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-id="element-645">
                    <div className="flex items-center gap-2" data-id="element-646">
                      <Thermometer className="w-4 h-4 text-orange-500" strokeWidth={2} data-id="element-647" />
                      <span className="text-gray-600" data-id="element-648">
                        Ref-Temp:{' '}
                        <strong data-id="element-649">{product.referenceTemperature}°C</strong>
                      </span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-650">
                      <Hash className="w-4 h-4 text-blue-500" strokeWidth={2} data-id="element-651" />
                      <span className="text-gray-600" data-id="element-652">
                        Prüfungen:{' '}
                        <strong data-id="element-653">{product.inspectionsPerSample}</strong>
                      </span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-654">
                      <Scale className="w-4 h-4 text-green-500" strokeWidth={2} data-id="element-655" />
                      <span className="text-gray-600" data-id="element-656">
                        Sollgewicht: <strong data-id="element-657">{product.targetWeight} kg</strong>
                      </span>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-658">
                      <Clock className="w-4 h-4 text-purple-500" strokeWidth={2} data-id="element-659" />
                      {product.timeIntervalEnabled ? <span className="text-gray-600" data-id="element-660">
                          Intervall:{' '}
                          <strong data-id="element-661">{product.timeIntervalMinutes} min</strong>
                        </span> : <span className="text-gray-500" data-id="element-662">
                          Intervall: <strong data-id="element-663">Deaktiviert</strong>
                        </span>}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4" data-id="element-664">
                  <button onClick={() => handleEditClick(product)} className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" aria-label="Bearbeiten" data-id="element-665">
                    <Edit2 className="w-4 h-4" strokeWidth={2} data-id="element-666" />
                  </button>
                  <button onClick={() => handleDeleteClick(product)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" aria-label="Löschen" data-id="element-667">
                    <Trash2 className="w-4 h-4" strokeWidth={2} data-id="element-668" />
                  </button>
                </div>
              </div>
            </motion.div>)}
      </div>

      {/* Add Product Modal */}
      <AnimatePresence data-id="element-669">
        {showAddModal && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/50 z-[60]" onClick={() => {
          setShowAddModal(false);
          resetForm();
        }} data-id="element-670" />
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-671">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.2
          }} className="w-full max-w-lg" data-id="element-672">
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto" data-id="element-673">
                  <div className="p-6" data-id="element-674">
                    <div className="flex justify-between items-start mb-6" data-id="element-675">
                      <h3 className="text-2xl font-semibold text-gray-900" data-id="element-676">
                        Produkt hinzufügen
                      </h3>
                      <button onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }} className="p-1 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-677">
                        <X className="w-5 h-5 text-gray-500" strokeWidth={2} data-id="element-678" />
                      </button>
                    </div>

                    <div className="space-y-4" data-id="element-679">
                      <div data-id="element-680">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-681">
                          Produktnummer
                        </label>
                        <input type="text" value={formData.productNumber} onChange={e => setFormData({
                      ...formData,
                      productNumber: e.target.value
                    })} placeholder="P-123123" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-682" />
                      </div>

                      <div data-id="element-683">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-684">
                          Produktname
                        </label>
                        <input type="text" value={formData.productName} onChange={e => setFormData({
                      ...formData,
                      productName: e.target.value
                    })} placeholder="Hähnchenbrust Premium" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-685" />
                      </div>

                      <div className="grid grid-cols-2 gap-4" data-id="element-686">
                        <div data-id="element-687">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-688">
                            Referenztemperatur (°C)
                          </label>
                          <input type="number" value={formData.referenceTemperature} onChange={e => setFormData({
                        ...formData,
                        referenceTemperature: parseFloat(e.target.value)
                      })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-689" />
                        </div>

                        <div data-id="element-690">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-691">
                            Prüfungen pro Stichprobe
                          </label>
                          <input type="number" value={formData.inspectionsPerSample} onChange={e => setFormData({
                        ...formData,
                        inspectionsPerSample: parseInt(e.target.value)
                      })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-692" />
                        </div>
                      </div>

                      <div data-id="element-693">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-694">
                          Sollgewicht (kg)
                        </label>
                        <input type="number" step="0.1" value={formData.targetWeight} onChange={e => setFormData({
                      ...formData,
                      targetWeight: parseFloat(e.target.value)
                    })} placeholder="2.5" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-695" />
                      </div>

                      <div className="border border-gray-200 rounded-lg p-4 space-y-3" data-id="element-696">
                        <div className="flex items-center gap-3" data-id="element-697">
                          <input type="checkbox" id="timeIntervalEnabled" checked={formData.timeIntervalEnabled} onChange={e => setFormData({
                        ...formData,
                        timeIntervalEnabled: e.target.checked
                      })} className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500" data-id="element-698" />
                          <label htmlFor="timeIntervalEnabled" className="text-sm font-medium text-gray-700 cursor-pointer" data-id="element-699">
                            Zeitintervall aktivieren
                          </label>
                        </div>

                        {formData.timeIntervalEnabled && <div data-id="element-700">
                            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-701">
                              Zeitintervall (Minuten)
                            </label>
                            <input type="number" value={formData.timeIntervalMinutes} onChange={e => setFormData({
                        ...formData,
                        timeIntervalMinutes: parseInt(e.target.value)
                      })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-702" />
                            <p className="text-xs text-gray-500 mt-1" data-id="element-703">
                              Wenn deaktiviert, können Prüfungen jederzeit
                              ausgelöst werden
                            </p>
                          </div>}
                      </div>
                    </div>

                    <div className="flex gap-3 mt-6" data-id="element-704">
                      <button onClick={() => {
                    setShowAddModal(false);
                    resetForm();
                  }} className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors" data-id="element-705">
                        Abbrechen
                      </button>
                      <button onClick={handleAddProduct} disabled={!formData.productNumber || !formData.productName} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-706">
                        Hinzufügen
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>

      {/* Edit Product Modal */}
      <AnimatePresence data-id="element-707">
        {showEditModal && selectedProduct && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/50 z-[60]" onClick={() => {
          setShowEditModal(false);
          setSelectedProduct(null);
          resetForm();
        }} data-id="element-708" />
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-709">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.2
          }} className="w-full max-w-lg" data-id="element-710">
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto" data-id="element-711">
                  <div className="p-6" data-id="element-712">
                    <div className="flex justify-between items-start mb-6" data-id="element-713">
                      <h3 className="text-2xl font-semibold text-gray-900" data-id="element-714">
                        Produkt bearbeiten
                      </h3>
                      <button onClick={() => {
                    setShowEditModal(false);
                    setSelectedProduct(null);
                    resetForm();
                  }} className="p-1 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-715">
                        <X className="w-5 h-5 text-gray-500" strokeWidth={2} data-id="element-716" />
                      </button>
                    </div>

                    <div className="space-y-4" data-id="element-717">
                      <div data-id="element-718">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-719">
                          Produktnummer
                        </label>
                        <input type="text" value={formData.productNumber} onChange={e => setFormData({
                      ...formData,
                      productNumber: e.target.value
                    })} placeholder="P-123123" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-720" />
                      </div>

                      <div data-id="element-721">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-722">
                          Produktname
                        </label>
                        <input type="text" value={formData.productName} onChange={e => setFormData({
                      ...formData,
                      productName: e.target.value
                    })} placeholder="Hähnchenbrust Premium" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-723" />
                      </div>

                      <div className="grid grid-cols-2 gap-4" data-id="element-724">
                        <div data-id="element-725">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-726">
                            Referenztemperatur (°C)
                          </label>
                          <input type="number" value={formData.referenceTemperature} onChange={e => setFormData({
                        ...formData,
                        referenceTemperature: parseFloat(e.target.value)
                      })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-727" />
                        </div>

                        <div data-id="element-728">
                          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-729">
                            Prüfungen pro Stichprobe
                          </label>
                          <input type="number" value={formData.inspectionsPerSample} onChange={e => setFormData({
                        ...formData,
                        inspectionsPerSample: parseInt(e.target.value)
                      })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-730" />
                        </div>
                      </div>

                      <div data-id="element-731">
                        <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-732">
                          Sollgewicht (kg)
                        </label>
                        <input type="number" step="0.1" value={formData.targetWeight} onChange={e => setFormData({
                      ...formData,
                      targetWeight: parseFloat(e.target.value)
                    })} placeholder="2.5" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-733" />
                      </div>

                      <div className="border border-gray-200 rounded-lg p-4 space-y-3" data-id="element-734">
                        <div className="flex items-center gap-3" data-id="element-735">
                          <input type="checkbox" id="timeIntervalEnabledEdit" checked={formData.timeIntervalEnabled} onChange={e => setFormData({
                        ...formData,
                        timeIntervalEnabled: e.target.checked
                      })} className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500" data-id="element-736" />
                          <label htmlFor="timeIntervalEnabledEdit" className="text-sm font-medium text-gray-700 cursor-pointer" data-id="element-737">
                            Zeitintervall aktivieren
                          </label>
                        </div>

                        {formData.timeIntervalEnabled && <div data-id="element-738">
                            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-739">
                              Zeitintervall (Minuten)
                            </label>
                            <input type="number" value={formData.timeIntervalMinutes} onChange={e => setFormData({
                        ...formData,
                        timeIntervalMinutes: parseInt(e.target.value)
                      })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-740" />
                            <p className="text-xs text-gray-500 mt-1" data-id="element-741">
                              Wenn deaktiviert, können Prüfungen jederzeit
                              ausgelöst werden
                            </p>
                          </div>}
                      </div>
                    </div>

                    <div className="flex gap-3 mt-6" data-id="element-742">
                      <button onClick={() => {
                    setShowEditModal(false);
                    setSelectedProduct(null);
                    resetForm();
                  }} className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors" data-id="element-743">
                        Abbrechen
                      </button>
                      <button onClick={handleUpdateProduct} disabled={!formData.productNumber || !formData.productName} className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-744">
                        Speichern
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>

      {/* Delete Confirmation Modal */}
      <AnimatePresence data-id="element-745">
        {showDeleteConfirm && selectedProduct && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/50 z-[60]" onClick={handleCancelDelete} data-id="element-746" />
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" data-id="element-747">
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.2
          }} className="w-full max-w-md" data-id="element-748">
                <div className="bg-white rounded-2xl shadow-2xl overflow-hidden" data-id="element-749">
                  <div className="p-6" data-id="element-750">
                    <div className="flex justify-between items-start mb-4" data-id="element-751">
                      <h3 className="text-2xl font-semibold text-gray-900" data-id="element-752">
                        Produkt löschen?
                      </h3>
                      <button onClick={handleCancelDelete} className="p-1 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-753">
                        <X className="w-5 h-5 text-gray-500" strokeWidth={2} data-id="element-754" />
                      </button>
                    </div>

                    <p className="text-gray-600 mb-6" data-id="element-755">
                      Möchten Sie das Produkt{' '}
                      <strong data-id="element-756">{selectedProduct.productName}</strong> wirklich
                      löschen?
                    </p>

                    <div className="flex gap-3" data-id="element-757">
                      <button onClick={handleCancelDelete} className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors" data-id="element-758">
                        Abbrechen
                      </button>
                      <button onClick={handleConfirmDelete} className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-colors" data-id="element-759">
                        Löschen
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </>}
      </AnimatePresence>
    </div>;
}